import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";
import LoggedByChip from "../ui/LoggedByChip";

const BottleTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sorted = useMemo(() => {
    return data.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await onDelete(id);
      } catch (err) {
        console.error("Error deleting:", err);
        alert("Failed to delete entry");
      }
    }
  };

  const totalAmount = sorted.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  const editFields = [
    { name: "date", label: "Date & Time", type: "datetime-local", required: true },
    { name: "amount", label: "Amount (oz)", type: "number", step: "0.1", required: true },
    { name: "type", label: "Type", type: "select", required: true, options: [
      { label: "Breast Milk", value: "Breast Milk" },
      { label: "Formula", value: "Formula" },
      { label: "Mixed", value: "Mixed" }
    ]},
    { name: "notes", label: "Notes", type: "textarea", rows: 2 },
  ];

  return (
    <>
      <div className="alert alert-info mb-3">
        <strong>Total Today:</strong> {totalAmount.toFixed(1)} oz
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                DateTime {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Amount</th>
              <th scope="col">Type</th>
              <th scope="col">Logged by</th>
              <th scope="col" style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.date)}</td>
                <td>{row.amount} oz</td>
                <td><span className="badge bg-info">{row.type}</span></td>
                <td><LoggedByChip loggedBy={row.loggedBy} /></td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Bottle Feed"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        isLoading={isSaving}
      />
    </>
  );
};

export default BottleTable;
